import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { isEqual, uniqBy, sumBy } from "lodash";
import moment from "moment";
import { DatePicker } from "antd";
import ReactTable from "react-table";
import MerchandiserInvoicingStatuses from "data/merchandiser-invoicing-statuses";
import filterCaseInsensitive from "components/custom/forms/react-table-case-insensitive";
import CustomCheckbox from "components/custom/forms/custom-checkbox";
import ReactTableFilterSelect from "components/custom/forms/react-table-filter-select";
import UserTypes from "components/auth/user-types";
import {
    patchCustomerChargeBillingHours
} from "api/menu/invoicing/actions";

class ListChargesCharges extends Component {
    dateFormat = "YYYY-MM-DD";

    constructor(props) {
        super(props);

        this.tableRef = React.createRef();

        this.handleUpdateBillableHours = this.handleUpdateBillableHours.bind(this);

        this.selectTable = null;
    }

    shouldComponentUpdate(nextProps, nextState) {
        const updatedChargesLength = nextProps.charges.filter((item, index) => {
            if (!this.props.charges[index]) {
                return true;
            } else if (!isEqual(item, this.props.charges[index])) {
                return true;
            }
            return false;
        });
        if (updatedChargesLength) {
            return true;
        } else if (!isEqual(nextProps.selectedCharges, this.props.selectedCharges)) {
            return true;
        } else if (nextProps.isAdminView !== this.props.isAdminView) {
            return true;
        }
        return false;
    }

    handleUpdateBillableHours = (invoiceSummaryId, value) => {
        this.props.patchCustomerChargeBillingHours(invoiceSummaryId, parseFloat(value));
    };

    getColumns = () => {
        const {
            isAdminView,
            charges,
            selectedCharges,
            auth
        } = this.props;
        const selectedChargesInvoiceSummaryIdArr = Object.keys(selectedCharges)
            .map(invoiceSummaryId => parseInt(invoiceSummaryId, 10));
        const selected = charges
            .filter(item => selectedChargesInvoiceSummaryIdArr.indexOf(item.invoiceSummaryId) > -1);

        const columns = [];

        columns.push({
            Cell: ({ original }) => (
                <CustomCheckbox
                    key={original.invoiceSummaryId}
                    className="checkbox"
                    checked={selectedCharges[original.invoiceSummaryId] === true}
                    disabled={original.payApprovalStatus ===
                        MerchandiserInvoicingStatuses.NotApprovedYet.key}
                    onChange={() => this.props.onToggle(original.invoiceSummaryId)}
                />
            ),
            headerClassName: "text-left",
            Header: () => (
                <input
                    type="checkbox"
                    className="checkbox"
                    checked={Object.keys(selectedCharges).length === charges.length
                    && Object.keys(selectedCharges).length}
                    ref={input => {
                        if (input) {
                            const selectedCount = Object.keys(selectedCharges).length;
                            input.indeterminate = charges.length > selectedCount
                                && selectedCount > 0;
                        }
                    }}
                    onChange={() => this.props.onToggle(false, this.tableRef)}
                />
            ),
            sortable: false,
            filterable: false,
            resizable: false,
            minResizeWidth: 40,
            width: 40,
            className: "d-flex justify-content-center align-items-center"
        });

        columns.push({
            Header: "Supplier",
            accessor: "supplierFullName"
        });
        columns.push({
            Header: "Job",
            accessor: "jobNumber"
        });
        columns.push({
            Header: "Merchandiser",
            accessor: "merchandiser",
        });
        columns.push({
            Header: "Retailer",
            accessor: "retailer",
            Footer: (
                <div className="">
                    <div className="mt-1">Totals</div>
                    <div className="mt-4">Selected</div>
                </div>
            )
        });
        columns.push({
            Header: "Store",
            accessor: "store",
            headerClassName: "header-store",
            Footer: (
                <div>
                    <div className="form-group">
                        <input
                            className="form-control form-control-sm"
                            type="number"
                            value={(uniqBy(charges, "storeId") || 0).length}
                            readOnly={true}
                        />
                    </div>
                    <div className="form-group">
                        <input
                            className="form-control form-control-sm"
                            type="number"
                            value={(uniqBy(selected, "storeId") || 0).length}
                            readOnly={true}
                        />
                    </div>
                </div>
            )
        });
        columns.push({
            Header: "Service Type",
            accessor: "serviceType"
        });
        columns.push({
            Header: "Product",
            accessor: "product"
        });
        columns.push({
            id: "completedDate",
            Header: "Date",
            accessor: original => moment(original.completedDate).format(this.dateFormat),
            Filter: ({ filter, onChange }) => (
                <DatePicker
                    Format={this.dateFormat}
                    value={filter ? filter.value : null}
                    onChange={date => onChange(date)}
                    placeholder=""
                />
            )
        });
        if (auth.user.role.includes(UserTypes.admin)) {
            columns.push({
                Header: "Charge",
                accessor: "chargeRate",
                Cell: ({ original }) => (typeof original.chargeRate === "number"
                    ? `$${original.chargeRate}`
                    : original.chargeRate
                )
            });
        }
        columns.push({
            Header: "Reported Hours",
            accessor: "originalHours",
            headerClassName: "header-originalHours",
            Footer: (
                <div>
                    <div className="form-group">
                        <input
                            className="form-control form-control-sm"
                            type="number"
                            value={(sumBy(charges, "originalHours") || 0).toFixed(2)}
                            readOnly={true}
                        />
                    </div>
                    <div className="form-group">
                        <input
                            className="form-control form-control-sm"
                            type="number"
                            value={(sumBy(selected, "originalHours") || 0).toFixed(2)}
                            readOnly={true}
                        />
                    </div>
                </div>
            )
        });
        columns.push({
            Header: "Billable Hours",
            accessor: "billableHours",
            headerClassName: "header-billableHours",
            Cell: ({ original }) => (
                <div key={original.chargeId}>
                    <input
                        className="form-control form-control-sm"
                        type="number"
                        min={0}
                        defaultValue={original.billableHours}
                        onBlur={ev => this.handleUpdateBillableHours(
                            original.invoiceSummaryId,
                            ev.target.value
                        )}
                    />
                </div>
            ),
            Footer: (
                <div>
                    <div className="form-group">
                        <input
                            className="form-control form-control-sm"
                            type="number"
                            value={(sumBy(charges, "billableHours") || 0).toFixed(2)}
                            readOnly={true}
                        />
                    </div>
                    <div className="form-group">
                        <input
                            className="form-control form-control-sm"
                            type="number"
                            value={(sumBy(selected, "billableHours") || 0).toFixed(2)}
                            readOnly={true}
                        />
                    </div>
                </div>
            )
        });
        columns.push({
            Header: "Billable Hours Adj.",
            accessor: "billableHoursAdj",
            headerClassName: "header-billableHoursAdj",
            Footer: (
                <div>
                    <div className="form-group">
                        <input
                            className="form-control form-control-sm"
                            type="number"
                            value={(sumBy(charges, "billableHoursAdj") || 0).toFixed(2)}
                            readOnly={true}
                        />
                    </div>
                    <div className="form-group">
                        <input
                            className="form-control form-control-sm"
                            type="number"
                            value={(sumBy(selected, "billableHoursAdj") || 0).toFixed(2)}
                            readOnly={true}
                        />
                    </div>
                </div>
            )
        });
        if (auth.user.role.includes(UserTypes.admin)) {
            columns.push({
                id: "amount",
                Header: "Amount",
                accessor: "amount",
                headerClassName: "header-amount",
                Cell: ({ original }) => (
                    original.amount < 0
                    ? `($${Math.abs(original.amount)})`
                    : `$${original.amount}`
                ),
                Footer: isAdminView ? (
                    <div>
                        <div className="form-group">
                            <input
                                className="form-control form-control-sm"
                                type="number"
                                value={sumBy(charges, "amount").toFixed(2)}
                                readOnly={true}
                            />
                        </div>
                        <div className="form-group">
                            <input
                                className="form-control form-control-sm"
                                type="number"
                                value={sumBy(selected, "amount").toFixed(2)}
                                readOnly={true}
                            />
                        </div>
                    </div>
                ) : null
            });
        }
        columns.push({
            id: "payApprovalStatus",
            Header: "Status",
            accessor: "payApprovalStatus",
            Cell: ({ original }) => (
                <div
                    className="text-center"
                    style={{
                        color: original.payApprovalStatus
                            ? MerchandiserInvoicingStatuses[original.payApprovalStatus].color
                            : ""
                    }}
                >
                    {original.payApprovalStatus
                        ? MerchandiserInvoicingStatuses[original.payApprovalStatus].icon
                        : ""}
                </div>
            ),
            Filter: ({ filter, onChange }) => {
                const options = {};
                options[""] = MerchandiserInvoicingStatuses.AllCharges.title;
                options.AllApproved = MerchandiserInvoicingStatuses.AllApproved.title;
                Object.keys(MerchandiserInvoicingStatuses).forEach(key => {
                    if (key !== "AllCharges") {
                        options[key] = MerchandiserInvoicingStatuses[key].title;
                    }
                });
                return (
                    <ReactTableFilterSelect
                        currentValue={filter && typeof filter.value !== "undefined" ? filter.value : ""}
                        onChange={value => onChange(value)}
                        style={{ width: "100%", height: "100%" }}
                        optionsObject={options}
                    />
                );
            },
            filterMethod: (filter, row) => {
                if (!filter.value || !filter.value.length) {
                    return true;
                }
                if (filter.value === MerchandiserInvoicingStatuses.AllApproved.key) {
                    return [
                        MerchandiserInvoicingStatuses.ApprovedAutomatically.key,
                        MerchandiserInvoicingStatuses.ApprovedByManager.key
                    ].indexOf(row.payApprovalStatus) > -1;
                }
                return filter.value.includes(row.payApprovalStatus);
            }
        });
        columns.push({
            id: "show-enter-report",
            Header: "Actions",
            Cell: ({ original }) => (
                <div>
                    {/*
                    <Button
                        className="p-1"
                        size="sm"
                        onClick={() => this.props.onViewReport(original.invoiceSummaryId)}
                    >
                        Show Report
                    </Button>
                    */}
                    <button
                        className="btn btn-sm btn-info ml-2"
                        onClick={() => this.props.onView(original.chargeId)}
                    >
                        Show Charge
                    </button>
                </div>
            ),
            Filter: () => false,
            width: 160
        });

        return columns.map(value => {
            if (value.headerClassName) {
                value.headerClassName = `${value.headerClassName} text-left`;
            } else {
                value.headerClassName = "text-left";
            }
            return value;
        });
    };

    render() {
        const { charges } = this.props;

        return (
            <div>
                <ReactTable
                    ref={this.tableRef}
                    data={charges}
                    columns={this.getColumns()}
                    headerClassName="table-header"
                    showPagination={true}
                    defaultPageSize={20}
                    pageSizeOptions={[20, 50, 100, 500, 1000]}
                    resizable
                    className="-striped -highlight"
                    filterable
                    defaultSorted={[{
                        id: "completedDate",
                        desc: false
                    }]}
                    defaultFilterMethod={filterCaseInsensitive}
                    getNoDataProps = {() => ({
                        style: {
                            zIndex: 0
                        }
                    })}
                    getTrProps={(state, rowInfo) => {
                        if (rowInfo && rowInfo.row) {
                            return {
                                style: {
                                    background: rowInfo.row.amount < 0 ? "#ffc0cb" : "",
                                }
                            };
                        }
                        return {};
                    }}
                    style={{
                        height: "calc(100vh - 240px)",
                    }}
                />
            </div>
        );
    }
}

ListChargesCharges.propTypes = {
    charges: PropTypes.array.isRequired,
    selectedCharges: PropTypes.object.isRequired,
    isAdminView: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
    onViewReport: PropTypes.func.isRequired,
    onView: PropTypes.func.isRequired,
    auth: PropTypes.object.isRequired
};

const mapStateToDispatch = dispatch => ({
    patchCustomerChargeBillingHours: bindActionCreators(patchCustomerChargeBillingHours, dispatch),
});

const mapStateToProps = state => ({
    auth: state.auth,
});

export default connect(
    mapStateToProps,
    mapStateToDispatch
)(ListChargesCharges);
